import {
  Alert,
  Button,
  DialogPosition,
  LoadingSpinner,
  MenuListDataItem,
  ScoreCard,
  StackedList,
  StackedListType,
  StatusChip,
  Typography,
  UserAlertListDataItem,
} from '@ecdlink/ui';
import { ReactComponent as Badge } from '@ecdlink/ui/src/assets/badge/badge_neutral.svg';
import { useCallback, useEffect, useMemo } from 'react';

import { useHistory, useLocation } from 'react-router';
import ROUTES from '@/routes/routes';
import { CommunityRouteState } from '../community.types';
import { useWindowSize } from '@reach/window-size';
import { useAppDispatch } from '@/store';
import { communitySelectors, communityThunkActions } from '@/store/community';
import { useSelector } from 'react-redux';
import { healthCareWorkerSelectors } from '@/store/healthCareWorker';
import { CommunityActions } from '@/store/community/community.actions';
import { useThunkFetchCall } from '@/hooks/useThunkFetchCall';
import {
  calculateClinicLeaguePositionPercentiles,
  calculateTierPercentages,
  getLeaguePointsColours,
  getTierDetails,
} from '@/utils/community/league-position';
import { LeagueType } from '@/constants/Community';

import { NoCommunityFound } from '../0-components/no-community-found';
import {
  getCommunityQuarterDescription,
  getStringFromClassNameOrId,
  useDialog,
  useSnackbar,
} from '@ecdlink/core';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import OnlineOnlyModal from '@/modals/offline-sync/online-only-modal';
import { TeamTabProps, TeamTabState } from './types';
import {
  COMMUNITY_WALKTHROUGH_STEPS,
  communityWalkthroughSteps,
} from '../walkthrough/steps';
import { useWalkthroughContext } from '@/context/walkthrougContext';
import { dummyClinic, dummyLeague } from '../walkthrough/community-dummy';
import { useCommunityTourAdvance } from '../walkthrough';

export const TeamTab: React.FC<TeamTabProps> = ({ forceReload }) => {
  const hcw = useSelector(healthCareWorkerSelectors.getHealthCareWorker);
  const realClinicDetails = useSelector(communitySelectors.getClinicSelector);
  const realLeague = useSelector(communitySelectors.getLeagueSelector);

  const history = useHistory();

  const { isOnline } = useOnlineStatus();
  const dialog = useDialog();

  const openOfflineDialog = useCallback(() => {
    dialog({
      color: 'bg-white',
      position: DialogPosition.Middle,
      render: (onSubmit) => (
        <OnlineOnlyModal
          onSubmit={() => {
            onSubmit();
          }}
        />
      ),
    });
  }, [dialog]);

  const { height } = useWindowSize();

  const appDispatch = useAppDispatch();

  const { showMessage } = useSnackbar();

  const { state } = useLocation<TeamTabState>();

  const {
    state: { run: isWalkthrough, stepIndex, tourActive },
  } = useWalkthroughContext();
  const advanceTour = useCommunityTourAdvance();

  // Use dummy data whenever the tour is preparing (tourActive) or running (run)
  const useWalkthroughData = isWalkthrough || tourActive;
  const clinicDetails = useWalkthroughData
    ? (dummyClinic as unknown as typeof realClinicDetails)
    : realClinicDetails;
  const league = useWalkthroughData
    ? (dummyLeague as unknown as typeof realLeague)
    : realLeague;

  const {
    isLoading: isLoadingClinic,
    wasLoading: wasLoadingClinic,
    isRejected: isRejectedClinic,
    error: errorClinic,
  } = useThunkFetchCall('community', CommunityActions.GET_CLINIC_BY_ID);
  const {
    isLoading: isLoadingLeague,
    wasLoading: wasLoadingLeague,
    isRejected: isRejectedLeague,
    error: errorLeague,
  } = useThunkFetchCall('community', CommunityActions.GET_LEAGUE_BY_ID);

  const isLoading = isLoadingClinic || (!league && isLoadingLeague);
  const wasLoading = wasLoadingLeague || wasLoadingClinic;
  const isRejected = isRejectedLeague || isRejectedClinic;
  const error = errorLeague || errorClinic;

  useEffect(() => {
    if (wasLoading && !isLoading && isRejected) {
      showMessage({
        message: error,
        type: 'error',
      });
    }
  }, [error, isLoading, isRejected, showMessage, wasLoading]);

  const memberCount = clinicDetails?.clinicMembers?.length ?? 0;

  const { isTop25PercentInTheLeague, isMiddle50PercentInTheLeague } =
    calculateClinicLeaguePositionPercentiles(
      league?.clinics ?? [],
      clinicDetails?.points?.leagueRanking ?? 0
    );

  const headerHeight = 122;

  const today = new Date();

  const { quarterDescription } = getCommunityQuarterDescription(today);

  const { tierName, tierColor } = getTierDetails(
    (clinicDetails?.league?.leagueTypeName as LeagueType) ?? LeagueType.League,
    clinicDetails?.points?.pointsTotal ?? 0
  );
  const { bronzePercentage, silverPercentage, goldPercentage } =
    calculateTierPercentages(
      (clinicDetails?.league?.leagueTypeName as LeagueType) ?? LeagueType.League
    );

  const leaguePointsColours = getLeaguePointsColours(
    isTop25PercentInTheLeague,
    isMiddle50PercentInTheLeague
  );

  useEffect(() => {
    if (!!hcw?.clinicId && !isLoadingClinic) {
      appDispatch(
        communityThunkActions.getClinicById({
          clinicId: hcw?.clinicId,
          forceReload: state?.forceReload ?? forceReload ?? true,
        })
      );
    }

    // trigger only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceReload]);

  const leaders: UserAlertListDataItem[] =
    clinicDetails?.teamLeads?.map((leader) => {
      const firstName = leader.firstName ?? '';
      const surname = leader.surname ?? '';
      return {
        title: `${firstName} ${surname}`.trim() || 'Team leader',
        titleStyle: 'text-textDark',
        profileDataUrl: '',
        profileText: `${firstName.charAt(0)}${surname.charAt(0)}` || 'TL',
        avatarColor: 'var(--primaryAccent2)',
        alertSeverity: 'none',
        hideAlertSeverity: true,
        onActionClick: () => {
          if (useWalkthroughData) {
            return;
          }
          history.push(
            ROUTES.COMMUNITY.TEAM.MEMBERS.LEADER_PROFILE.replace(
              ':leaderId',
              leader.id
            ),
            { isFromTeamTab: true } as TeamTabState
          );
        },
      };
    }) ?? [];

  const leagueCard: MenuListDataItem = useMemo(
    () => ({
      title: `in the league ${isTop25PercentInTheLeague ? '🥳' : ''}`,
      titleStyle: 'text-textDark',
      onActionClick: () => {
        if (useWalkthroughData) {
          // Pause Joyride, go to league tab; resume when step-1 target mounts
          advanceTour(COMMUNITY_WALKTHROUGH_STEPS.TWO);
        }

        history.push(ROUTES.COMMUNITY.ROOT, {
          activeTabIndex: 1,
        } as CommunityRouteState);
      },
      customIcon: (
        <div className="relative mr-4 flex h-11 w-11 items-center justify-center">
          <Badge
            className="absolute z-0 h-auto w-auto"
            fill={`var(--${leaguePointsColours.mainColour})`}
          />
          <Typography
            className="relative z-10"
            color="white"
            type="h1"
            text={String(clinicDetails?.points?.leagueRanking ?? 0)}
          />
        </div>
      ),
      backgroundColor: leaguePointsColours.backgroundColour,
    }),
    [
      advanceTour,
      clinicDetails?.points?.leagueRanking,
      history,
      isTop25PercentInTheLeague,
      useWalkthroughData,
      leaguePointsColours.backgroundColour,
      leaguePointsColours.mainColour,
    ]
  );

  if (isLoading && !useWalkthroughData) {
    return (
      <LoadingSpinner
        className="mt-6"
        size="medium"
        spinnerColor="primary"
        backgroundColor="uiLight"
      />
    );
  }

  if (!clinicDetails) {
    return <NoCommunityFound />;
  }

  return (
    <div
      className={`${
        (stepIndex === 0 || stepIndex === 2) && useWalkthroughData
          ? 'overflow-hidden'
          : 'overflow-auto'
      } p-4 pt-6`}
      style={{ height: height - headerHeight }}
    >
      <div className="flex h-full flex-col">
        <Typography type="h2" text={clinicDetails?.name} />
        <Typography type="h4" color="textMid" text={quarterDescription} />
        <div className="mt-4 flex items-center justify-start gap-2">
          <StatusChip
            className="h-7"
            backgroundColour="successMain"
            borderColour="successMain"
            textColour="white"
            text={`${memberCount} ${memberCount === 1 ? 'member' : 'members'}`}
            iconPosition="start"
          />
          <StatusChip
            className="h-7"
            backgroundColour={tierColor}
            borderColour={tierColor}
            textColour="white"
            text={tierName}
            iconPosition="start"
            icon="StarIcon"
          />
        </div>
        {!!clinicDetails?.league && (
          <div className="my-7">
            <Typography
              className="mb-5"
              type="h3"
              text="League position & points"
            />
            {/* Explicit wrappers so Joyride can spotlight a real box (not a zero-size edge case) */}
            <div
              id={getStringFromClassNameOrId(
                communityWalkthroughSteps[COMMUNITY_WALKTHROUGH_STEPS.ONE]
                  .target
              )}
            >
              <StackedList
                isFullHeight={false}
                type={'MenuList' as StackedListType}
                listItems={[leagueCard]}
              />
            </div>
            <div
              id={getStringFromClassNameOrId(
                communityWalkthroughSteps[COMMUNITY_WALKTHROUGH_STEPS.THREE]
                  .target
              )}
              className="mt-2"
            >
              <ScoreCard
                className="mt-0"
                mainText={String(clinicDetails?.points?.pointsTotal ?? 0)}
                hint="points"
                currentPoints={clinicDetails?.points?.pointsTotal ?? 0}
                maxPoints={clinicDetails?.points?.maxPointsTotal ?? 0}
                barBgColour="uiLight"
                barColour={tierColor}
                bgColour="uiBg"
                barSize="medium"
                barDivides={[
                  { widthPercentage: bronzePercentage },
                  { widthPercentage: silverPercentage },
                  { widthPercentage: goldPercentage },
                ]}
                barStatusChip={{
                  backgroundColour: 'primary',
                  borderColour: 'primary',
                  textColour: 'white',
                  text: tierName,
                }}
                textColour="black"
                onClick={() => {
                  if (useWalkthroughData) {
                    // Pause Joyride, open points page; resume when step-3 target mounts
                    advanceTour(COMMUNITY_WALKTHROUGH_STEPS.FOUR);
                  }

                  history.push(ROUTES.COMMUNITY.TEAM.POINTS.ROOT);
                }}
              />
            </div>
          </div>
        )}
        <div
          id={getStringFromClassNameOrId(
            communityWalkthroughSteps[COMMUNITY_WALKTHROUGH_STEPS.FIVE].target
          )}
        >
          <Typography
            className="mb-5"
            type="h3"
            text={`Team leader${leaders.length > 1 ? 's' : ''}`}
          />
          {!!leaders.length ? (
            <div className="mb-4">
              <StackedList
                className="flex flex-col gap-2"
                isFullHeight={false}
                type={'UserAlertList' as StackedListType}
                listItems={leaders}
              />
            </div>
          ) : (
            <Alert type="warning" title="No team leader" />
          )}
        </div>
        <div className={`mt-auto flex flex-col gap-4 pt-4`}>
          <Button
            id={getStringFromClassNameOrId(
              communityWalkthroughSteps[COMMUNITY_WALKTHROUGH_STEPS.SIX].target
            )}
            icon="UserGroupIcon"
            type="filled"
            textColor="white"
            color="primary"
            text="See team members"
            onClick={() => history.push(ROUTES.COMMUNITY.TEAM.MEMBERS.ROOT)}
          />
          <Button
            className="mb-4"
            icon="LightBulbIcon"
            type="outlined"
            textColor="primary"
            color="primary"
            text="How to earn points"
            onClick={() =>
              isOnline
                ? history.push(ROUTES.COMMUNITY.TEAM.INFO_PAGE, {
                    isFromTeamTab: true,
                  } as TeamTabState)
                : openOfflineDialog()
            }
          />
        </div>
      </div>
      <div
        id={getStringFromClassNameOrId(
          communityWalkthroughSteps[COMMUNITY_WALKTHROUGH_STEPS.NINE].target
        )}
        className="absolute bottom-0 w-full"
      />
    </div>
  );
};
